import { Popup } from "components/popups/Popup";
import Button from "components/shared/button";
import Input from "components/shared/inputs/input";
import { useGetUserInfoQuery, useUpdateUserInfoMutation } from "store/api";
import useCustomForm from "utils/hooks/useCustomForm";
import useTranslation from "utils/hooks/useTranslation";
import Loader from "components/shared/loader";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { addNotification } from "store/componentsSlice";
import { useDispatch } from "react-redux";

// const defaultValues = {
//     lastname: '',
//     firstname: '',
//     address1: '',
//     address2: '',
//     city: '',
//     postalcode: '',
//     siret: '',
//     phone: '',
//     fax: '',
//     email: '',
//     www: '',
//     comment: '',
//     image: '',
//     energy: {}
// };

type ProfileSchema = Pick<UserInfo, 'lastname' | 'firstname'>;

export const ProfilePopup: React.FC<{
    closePopup: any;
}> = ({
    closePopup,
}) => {

        const { t } = useTranslation();
        const dispatch = useDispatch();
        const { data: UserInfoData, refetch } = useGetUserInfoQuery(undefined);
        const [updateUserInfo, { isLoading }] = useUpdateUserInfoMutation();

        const schema: yup.ObjectSchema<ProfileSchema> = yup.object({
            lastname: yup.string().required(t("Lastname name is required")),
            firstname: yup.string().required(t("Firstname name is required")),
            // phone: yup.string().notRequired().strict(),
            // fax: yup.string().notRequired().strict(),
            // address1: yup.string().notRequired().strict(),
            // address2: yup.string().notRequired().strict(),
            // city: yup.string().notRequired().strict(),
            // postalcode: yup.string().notRequired().strict(),
            // email: yup.string().notRequired().strict(),
            // www: yup.string().notRequired().strict(),
            // comment: yup.string().notRequired().strict(),
            // comment: yup.string().notRequired().default(''),
        });

        const {
            handleSubmit,
            reset,
            control,
            getValues,
            formState: { errors },
        } = useCustomForm<UserInfo>(
            {
                defaultValues: UserInfoData,
                resolver: yupResolver(schema),
            }
        );

        const onSubmit = async () => {
            const userInfoData = getValues();
            try {
                await updateUserInfo(userInfoData).unwrap();
                dispatch(addNotification({
                    type: 'success',
                    message: t('UPDATED_SUCCESSFULLY'),
                }));

                // 🔁 Force refresh of userInfo from backend
                const refreshed = await refetch();

                if (refreshed.data) {
                    reset(refreshed.data); // ✅ Update form with true backend state
                }
                closePopup();
            } catch (error) {
                dispatch(addNotification({
                    type: 'error',
                    message: t('SOMETHING_WENT_WRONG'),
                }));
                console.error(error);
            }
        };

        return (
            <Popup
                title={t("ACCOUNT_PERSONAL_INFO")}
                translateTitle
                fixSize={"medium"}
                commandPanelProps={{
                    additionalElement: (
                        <group data-justify="start" data-gap="20">
                            <Button
                                data-length="forcefit"
                                accent
                                secondary
                                large
                                onClick={() => {
                                    closePopup();
                                }}
                            >
                                {t("CANCEL")}
                            </Button>
                            <Button
                                data-length="forcefit"
                                secondary
                                large
                                onClick={handleSubmit(onSubmit)}
                            >
                                {t("NAV_SAVE")}
                            </Button>
                        </group>
                    )
                }}
            >
                {isLoading && <Loader />}
                <group
                    data-direction="column"
                    data-gap="20"
                    data-space="40"
                    data-space-horizontal="40"
                    data-space-top="5"
                    data-height="autofit"
                >
                    <group data-gap="10">
                        <text data-weight="400" data-text-size="14" data-ellipsis="" data-color="grey-color-dark"><i data-space-horizontal="5" data-color="red">*</i> {t("REQUIRED_FIELDS")}</text>
                    </group>
                    <group data-gap="20">
                        <group data-gap="20">
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_FIRSTNAME"}
                                    requiredLabel={true}
                                    control={control}
                                    name="firstname"
                                    type="text"
                                    dataMaterial
                                    error={errors.firstname?.message}
                                />
                            </group>
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_LASTNAME"}
                                    requiredLabel={true}
                                    control={control}
                                    name="lastname"
                                    type="text"
                                    dataMaterial
                                    error={errors.lastname?.message}
                                />
                            </group>
                        </group>
                        <group data-gap="20">
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_PHONE"}
                                    // requiredLabel={true}
                                    control={control}
                                    name="phone"
                                    type="text"
                                    dataMaterial
                                />
                            </group>
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_FAX"}
                                    // requiredLabel={true}
                                    control={control}
                                    name="fax"
                                    type="text"
                                    dataMaterial
                                />
                            </group>
                        </group>

                        <group data-gap="20">
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_ADDRESS1"}
                                    // requiredLabel={true}
                                    control={control}
                                    name="address1"
                                    type="text"
                                    dataMaterial
                                />
                            </group>
                        </group>

                        <group data-gap="20">
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_ADDRESS2"}
                                    // requiredLabel={true}
                                    control={control}
                                    name="address2"
                                    type="text"
                                    dataMaterial
                                />
                            </group>
                        </group>

                        <group data-gap="20">
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_CITY"}
                                    // requiredLabel={true}
                                    control={control}
                                    name="city"
                                    type="text"
                                    dataMaterial
                                />
                            </group>
                        </group>

                        <group data-gap="20">
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_POSTALCODE"}
                                    // requiredLabel={true}
                                    control={control}
                                    name="postalcode"
                                    type="text"
                                    dataMaterial
                                />
                            </group>
                        </group>

                        <group data-gap="20">
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_NUMBER"}
                                    // requiredLabel={true}
                                    control={control}
                                    name="siret"
                                    type="text"
                                    dataMaterial
                                />
                            </group>
                        </group>

                        <group data-gap="20">
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_EMAIL"}
                                    // requiredLabel={true}
                                    control={control}
                                    name="email"
                                    type="text"
                                    dataMaterial
                                />
                            </group>
                        </group>

                        <group data-gap="20">
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_WWW"}
                                    // requiredLabel={true}
                                    control={control}
                                    name="www"
                                    type="text"
                                    dataMaterial
                                />
                            </group>
                        </group>

                        <group data-gap="20">
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_POLICY_NUMBER"}
                                    // requiredLabel={true}
                                    control={control}
                                    name="policyNumber"
                                    type="text"
                                    dataMaterial
                                />
                            </group>
                        </group>

                        <group data-gap="20">
                            <group data-length="forcefit" data-fit="1">
                                <Input
                                    dataLength="autofit"
                                    label={"ACCOUNT_COMMENT"}
                                    // requiredLabel={true}
                                    control={control}
                                    name="comment"
                                    type="textarea"
                                    dataMaterial
                                />
                            </group>
                        </group>
                    </group>
                </group>
            </Popup>
        )
    };